import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { getDoctorAuditDetailsAPI } from "../../../Api/Audit";

interface AuditDetail {
  action: string;
  remark: string | null;
  createdAt: string;
  actionBy:string;
}

interface DoctorProfileTATProps {
  doctorId: number;
  doctorName?: string;
  isOpen: boolean;
  onClose: () => void;
}

// ✅ NEW: Filter state interface
interface FilterState {
  action: string;
  remark: string;
  actionBy: string;   // ✅ NEW
  createdAt: string;
}


const DoctorProfileTAT: React.FC<DoctorProfileTATProps> = ({ 
  doctorId, 
  doctorName = "Doctor", 
  isOpen, 
  onClose 
}) => {
  const [auditDetails, setAuditDetails] = useState<AuditDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  
  // ✅ NEW: Filter state
 const [filters, setFilters] = useState<FilterState>({
  action: "",
  remark: "",
  actionBy: "",     // ✅ NEW
  createdAt: ""
});


  // ✅ NEW: Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AuditDetail | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  const fetchAuditDetails = async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await getDoctorAuditDetailsAPI(doctorId)
      if (response?.isSuccess && Array.isArray(response.data)){
        setAuditDetails(response.data);
      } else {
        setAuditDetails([]);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch audit details");
      setAuditDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && doctorId) {
      fetchAuditDetails();
    }
  }, [isOpen, doctorId]);

  const filteredAuditDetails = auditDetails.filter((detail) => {
  const matchesAction = detail.action
    ?.toLowerCase()
    .includes(filters.action.toLowerCase());

  const matchesRemark =
    detail.remark?.toLowerCase().includes(filters.remark.toLowerCase()) ||
    (!detail.remark && filters.remark === "");

  const matchesActionBy = detail.actionBy
    ?.toLowerCase()
    .includes(filters.actionBy.toLowerCase());

  const matchesCreatedAt = filters.createdAt
    ? dayjs(detail.createdAt).format("YYYY-MM-DD") === filters.createdAt
    : true;

  return (
    matchesAction &&
    matchesRemark &&
    matchesActionBy &&
    matchesCreatedAt
  );
});



  const sortedAuditDetails = React.useMemo(() => {
    if (!sortConfig.key) return filteredAuditDetails;

    return [...filteredAuditDetails].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredAuditDetails, sortConfig]);

  // ✅ NEW: Handle sort
  const handleSort = (key: keyof AuditDetail) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // ✅ NEW: Sort icon component
  const sortIcon = (columnKey: keyof AuditDetail) => {
    if (sortConfig.key !== columnKey) {
      return <span className="opacity-50">⇅</span>;
    }
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  if (!isOpen) return null;

  const getActionColor = (action: string) => {
    switch (action?.toLowerCase()) {
      case "approvedreject":
        return "bg-green-100 text-green-800 border border-green-200";
      case "statustimestamp":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      case "getdoctorsbyid":
        return "bg-indigo-100 text-indigo-800 border border-indigo-200";
      case "getalldoctors":
        return "bg-purple-100 text-purple-800 border border-purple-200";
      case "login":
        return "bg-orange-100 text-orange-800 border border-orange-200";
      case "languages":
      case "designation":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const formatDateTime = (dateString: string) => {
    return dayjs(dateString).format("DD MMM YY, HH:mm:ss");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col animate-slideUp">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#184A84] to-[#1e5ba0] text-white px-5 py-2 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div>
            
              <p className="text-blue-100">
                Doctor ID: <span className="font-semibold">{doctorId}</span> | 
                {doctorName && (
                  <>
                    {" "}•{" "}
                    <span className="font-semibold">{doctorName}</span>
                  </>
                )}
              </p>
              <p className="text-sm text-blue-200 mt-1">
                Total Records: <span className="font-bold">{auditDetails.length}</span> 
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

     
        
  
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 p-5">
            <div className="overflow-x-auto">
              <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#184A84] text-white sticky top-0 z-20">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold w-16">S.No</th>
                      <th 
                        className="px-3 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600 transition-colors group" 
                        onClick={() => handleSort("action")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Action</span>
                          <span className="text-blue-300 group-hover:text-blue-100">
                            {sortIcon("action")}
                          </span>
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600 transition-colors group" 
                        onClick={() => handleSort("remark")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Remark</span>
                          <span className="text-blue-300 group-hover:text-blue-100">
                            {sortIcon("remark")}
                          </span>
                        </div>
                      </th>
                      <th
  className="px-3 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600 transition-colors group"
  onClick={() => handleSort("actionBy")}
>
  <div className="flex items-center gap-2">
    <span>Action By</span>
    <span className="text-blue-300 group-hover:text-blue-100">
      {sortIcon("actionBy")}
    </span>
  </div>
</th>

                      <th 
                        className="px-3 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600 transition-colors group whitespace-nowrap" 
                        onClick={() => handleSort("createdAt")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Created On</span>
                          <span className="text-blue-300 group-hover:text-blue-100">
                            {sortIcon("createdAt")}
                          </span>
                        </div>
                      </th>
                    </tr>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <th className="px-1 py-1 w-16"></th>
                      <th className="px-1 py-1">
                        <input 
                          type="text" 
                          placeholder="Filter by action..." 
                          value={filters.action} 
                          onChange={e => setFilters(prev => ({ ...prev, action: e.target.value }))} 
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                        />
                      </th>
                      <th className="px-1 py-1">
                        <input 
                          type="text" 
                          placeholder="Filter by remark..." 
                          value={filters.remark} 
                          onChange={e => setFilters(prev => ({ ...prev, remark: e.target.value }))} 
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                        />
                      </th>
                      <th className="px-1 py-1">
  <input
    type="text"
    placeholder="Filter by action by..."
    value={filters.actionBy}
    onChange={e =>
      setFilters(prev => ({ ...prev, actionBy: e.target.value }))
    }
    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
  />
</th>

                      <th className="px-1 py-1 whitespace-nowrap">
                        <input 
                          type="date" 
                          value={filters.createdAt} 
                          onChange={e => setFilters(prev => ({ ...prev, createdAt: e.target.value }))} 
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-16 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#184A84]"></div>
                            <p className="text-gray-500">Loading audit details...</p>
                          </div>
                        </td>
                      </tr>
                    ) : sortedAuditDetails.length > 0 ? (
                      sortedAuditDetails.map((detail, index) => (
                        <tr 
                          key={`${detail.action}-${detail.createdAt}-${index}`}
                          className="hover:bg-blue-50 transition-colors even:bg-gray-50"
                        >
                          <td className="px-3 py-3 font-medium text-gray-900 w-16 text-center">
                            {index + 1}
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${getActionColor(detail.action)}`}>
                              {detail.action || "N/A"}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-900 max-w-md align-top">
  <div className="whitespace-pre-wrap break-words leading-relaxed">
    {detail.remark || "-"}
  </div>
</td>
<td className="px-3 py-3 text-sm text-gray-800 font-medium">
  {detail.actionBy || "-"}
</td>

                          <td className="px-3 py-3 text-sm text-gray-700 font-mono whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span title={detail.createdAt}>{formatDateTime(detail.createdAt)}</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
<td colSpan={5} className="px-6 py-16 text-center">
                          <div className="flex flex-col items-center gap-4">
                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <p className="text-2xl font-bold text-gray-500">
                                {auditDetails.length === 0 ? "No Audit Records" : "No Matching Records"}
                              </p>
                              <p className="text-gray-400 text-sm mt-1">
                                {auditDetails.length === 0 
                                  ? "No activity found for this doctor" 
                                  : "Try adjusting your filters"}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorProfileTAT;
